// Project Summary Report PDF — multi-page, share-ready PDF built from
// existing project data. Uses jsPDF + jspdf-autotable.
//
// Public API:
//   await generateProjectReport({ project, currency, suggestedOffer, timeline })
//   classifyFeasibility({ roi })  → { tone, label }

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  estimatedRenovationCost, estimatedSoftCosts, totalProjectCost,
  estimatedProfit, estimatedROI, margin, formatCurrency,
  estimatedInteriorCost, estimatedExteriorCost, estimatedSystemsCost,
  estimatedExtensionsCost, projectGLA, extensionsAddedSqm,
  roomTotals, workItemTotal,
} from "@/lib/calculations";
import { exteriorItemTotal } from "@/lib/exteriorTemplates";
import { softCostItemTotal } from "@/lib/softCostsTemplates";
import { constructionSummary } from "@/lib/constructionTypes";

const LOGO_URL = "/brand/flipcost-logo.png";

// Standard ROI feasibility bands.
export function classifyFeasibility({ roi }) {
  if (!isFinite(roi)) return { tone: "red", label: "Not Feasible" };
  if (roi >= 0.20) return { tone: "green", label: "Feasible" };
  if (roi >= 0.10) return { tone: "amber", label: "Tight / Break-even" };
  return { tone: "red", label: "Not Feasible" };
}

const STRATEGY_LABEL = {
  flip: "Flip for profit",
  sell: "Renovate to sell",
  live: "Renovate to live",
};

let _logoDataUrlCache = null;
async function loadLogoDataUrl() {
  if (_logoDataUrlCache !== null) return _logoDataUrlCache;
  try {
    const res = await fetch(LOGO_URL);
    if (!res.ok) throw new Error(`logo ${res.status}`);
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    _logoDataUrlCache = dataUrl;
    return dataUrl;
  } catch {
    _logoDataUrlCache = false;
    return false;
  }
}

function stripCurrencySymbol(formatted) {
  if (typeof formatted !== "string") return String(formatted ?? "");
  return formatted.replace(/^[^\d-]+/, "").trim();
}

function fmt(currency) { return (n) => formatCurrency(n, currency); }

const COLORS = {
  ink: [28, 28, 28],
  body: [87, 83, 78],
  muted: [120, 113, 108],
  hairline: [218, 215, 211],
  bgSoft: [247, 247, 246],
  sage: [88, 113, 96],
  sageBg: [232, 240, 234],
  amber: [180, 121, 23],
  amberBg: [253, 240, 215],
  red: [183, 47, 47],
  redBg: [254, 232, 232],
  green: [22, 122, 79],
  greenBg: [219, 240, 228],
};

function tone(c) {
  switch (c) {
    case "green": return { fg: COLORS.green, bg: COLORS.greenBg };
    case "amber": return { fg: COLORS.amber, bg: COLORS.amberBg };
    case "red":   return { fg: COLORS.red,   bg: COLORS.redBg };
    default:      return { fg: COLORS.ink,   bg: COLORS.bgSoft };
  }
}

const PAGE_MARGIN = 40;
const FOOTER_RESERVE = 56; // px reserved at the bottom of every page for the disclaimer/footer

// Returns the cursorY at which a section should start, adding a new page if
// the requested vertical space wouldn't fit in the remaining body area.
function ensureSpace(doc, cursorY, needed) {
  const pageH = doc.internal.pageSize.getHeight();
  if (cursorY + needed > pageH - FOOTER_RESERVE) {
    doc.addPage();
    return PAGE_MARGIN;
  }
  return cursorY;
}

export async function generateProjectReport({
  project, currency, suggestedOffer = 0, timeline = 0,
}) {
  const cy = currency || project.currency || "USD";
  const f = fmt(cy);

  const purchase = Number(project.purchase_price) || 0;
  const arv = Number(project.target_sale_price) || 0;
  const interior = estimatedInteriorCost(project);
  const exterior = estimatedExteriorCost(project);
  const systems = estimatedSystemsCost(project);
  const extensions = estimatedExtensionsCost(project);
  const reno = estimatedRenovationCost(project);
  const soft = estimatedSoftCosts(project);
  const total = totalProjectCost(project);
  const profit = estimatedProfit(project);
  const roi = estimatedROI(project);
  const m = margin(project);
  const gla = projectGLA(project);
  const addedSqm = extensionsAddedSqm(project);

  const feasibility = classifyFeasibility({ roi });

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = PAGE_MARGIN;
  const CONTENT_W = PAGE_W - M * 2;

  // --- Header (page 1 only) ---------------------------------------
  let y = M;
  const logoData = await loadLogoDataUrl();
  if (logoData) {
    try { doc.addImage(logoData, "PNG", M, y - 4, 36, 36); } catch { /* noop */ }
  }
  doc.setTextColor(...COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("FlipCost AI", M + (logoData ? 46 : 0), y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.muted);
  doc.text("Renovation Decision Engine", M + (logoData ? 46 : 0), y + 24);

  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.muted);
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  doc.text(`Generated ${today}`, PAGE_W - M, y + 12, { align: "right" });
  doc.text(`Currency: ${cy}`, PAGE_W - M, y + 24, { align: "right" });

  y += 44;
  doc.setDrawColor(...COLORS.hairline);
  doc.setLineWidth(0.6);
  doc.line(M, y, PAGE_W - M, y);
  y += 18;

  // --- Title block ------------------------------------------------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.ink);
  doc.text("Project Summary Report", M, y);
  y += 22;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...COLORS.body);
  doc.text(project.name || "Untitled Project", M, y);
  y += 14;
  if (project.address) {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text(project.address, M, y);
    y += 14;
  }

  y += 2;
  let pillX = M;
  const strategy = STRATEGY_LABEL[project.project_goal] || STRATEGY_LABEL.flip;
  pillX = drawPill(doc, pillX, y - 9, `Strategy · ${strategy}`, COLORS.sage, COLORS.sageBg);
  pillX += 8;
  if (timeline > 0) {
    pillX = drawPill(doc, pillX, y - 9, `Timeline · ${timeline} months`, COLORS.sage, COLORS.sageBg);
    pillX += 8;
  }
  if (project.floor_area_sqm) {
    pillX = drawPill(doc, pillX, y - 9, `${project.floor_area_sqm} sqm floor area`, COLORS.muted, COLORS.bgSoft);
    pillX += 8;
  }
  if (addedSqm > 0) {
    drawPill(doc, pillX, y - 9, `+${addedSqm} sqm extension`, COLORS.muted, COLORS.bgSoft);
  }
  y += 22;

  // --- Project Overview ------------------------------------------
  y = ensureSpace(doc, y, 130);
  y = sectionTitle(doc, "Project Overview", M, y);
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M, bottom: FOOTER_RESERVE },
    theme: "plain",
    styles: { fontSize: 9.5, cellPadding: { top: 5, bottom: 5, left: 0, right: 0 }, textColor: COLORS.body },
    columnStyles: {
      0: { cellWidth: CONTENT_W * 0.42, textColor: COLORS.muted },
      1: { cellWidth: CONTENT_W * 0.58, textColor: COLORS.ink, fontStyle: "bold", halign: "right" },
    },
    body: [
      ["Asking / Purchase price", f(purchase)],
      ["Estimated post-renovation value (ARV)", f(arv)],
      ["Property type", titleCase(project.property_type)],
      ["Renovation type", titleCase(project.renovation_type)],
      ...(constructionSummary(project) ? [["Construction", constructionSummary(project)]] : []),
      ["Existing floor area", project.floor_area_sqm ? `${project.floor_area_sqm} sqm` : "—"],
      ...(addedSqm > 0 ? [["Added by extensions", `${addedSqm} sqm`]] : []),
      ...(addedSqm > 0 ? [["Post-build GLA", `${gla} sqm`]] : []),
      ...(timeline > 0 ? [["Estimated timeline", `${timeline} months`]] : []),
    ],
  });
  y = doc.lastAutoTable.finalY + 14;

  // --- Cost Breakdown summary ------------------------------------
  y = ensureSpace(doc, y, 200);
  y = sectionTitle(doc, "Cost Breakdown", M, y);
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M, bottom: FOOTER_RESERVE },
    theme: "plain",
    headStyles: {
      fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
      cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
    },
    bodyStyles: {
      fontSize: 9.5, textColor: COLORS.ink,
      cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      lineColor: COLORS.hairline, lineWidth: { bottom: 0.4 },
    },
    columnStyles: {
      0: { cellWidth: CONTENT_W * 0.55 },
      1: { cellWidth: CONTENT_W * 0.45, halign: "right", fontStyle: "bold" },
    },
    head: [["Line", `Amount (${cy})`]],
    body: [
      ["Interior (rooms)", stripCurrencySymbol(f(interior))],
      ["Exterior", stripCurrencySymbol(f(exterior))],
      ["Systems", stripCurrencySymbol(f(systems))],
      ...(extensions > 0 ? [["Extensions", stripCurrencySymbol(f(extensions))]] : []),
      ["Renovation subtotal", stripCurrencySymbol(f(reno))],
      ["Soft costs (agent, holding, contingency)", stripCurrencySymbol(f(soft))],
      ["Purchase price", stripCurrencySymbol(f(purchase))],
    ],
    foot: [["Total project cost", stripCurrencySymbol(f(total))]],
    footStyles: {
      fillColor: COLORS.ink, textColor: [255, 255, 255], fontSize: 10, fontStyle: "bold",
      cellPadding: { top: 9, bottom: 9, left: 8, right: 8 },
    },
  });
  y = doc.lastAutoTable.finalY + 18;

  // --- Total Cost vs ARV bar -------------------------------------
  y = ensureSpace(doc, y, 90);
  y = sectionTitle(doc, "Total Cost vs Market Estimate (ARV)", M, y);
  y = drawCostVsARV(doc, M, y, CONTENT_W, total, arv, f);
  y += 12;

  // --- Feasibility ------------------------------------------------
  y = ensureSpace(doc, y, 130);
  y = sectionTitle(doc, "Feasibility Summary", M, y);
  y = drawFeasibility(doc, M, y, CONTENT_W, { profit, roi, margin: m, feasibility, f });
  y += 18;

  // --- Detailed breakdowns (paginate as needed) ------------------
  // 1) Rooms
  const roomRows = (project.rooms || []).map((r) => {
    const t = roomTotals(r).total;
    return [r.name || titleCase(r.room_type), titleCase(r.room_type), `${r.size_sqm || 0} sqm`, titleCase(r.finish_level), stripCurrencySymbol(f(t))];
  });
  if (roomRows.length > 0) {
    y = ensureSpace(doc, y, 80);
    y = sectionTitle(doc, "Rooms — line by line", M, y);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: FOOTER_RESERVE },
      theme: "plain",
      headStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
        lineColor: COLORS.hairline, lineWidth: { bottom: 0.3 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.30 },
        1: { cellWidth: CONTENT_W * 0.18, textColor: COLORS.muted },
        2: { cellWidth: CONTENT_W * 0.14, textColor: COLORS.muted, halign: "right" },
        3: { cellWidth: CONTENT_W * 0.14, textColor: COLORS.muted, halign: "right" },
        4: { cellWidth: CONTENT_W * 0.24, halign: "right", fontStyle: "bold" },
      },
      head: [["Room", "Type", "Size", "Finish", `Total (${cy})`]],
      body: roomRows,
      foot: [["", "", "", "Interior subtotal", stripCurrencySymbol(f(interior))]],
      footStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.ink, fontSize: 9, fontStyle: "bold",
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // 2) Exterior items
  const extRows = (project.exterior || []).filter((it) => it.enabled).map((it) => [
    it.label || titleCase(it.category),
    `${it.quantity || 0} ${it.unit || ""}`,
    titleCase(it.scope),
    stripCurrencySymbol(f(exteriorItemTotal(it))),
  ]);
  if (extRows.length > 0) {
    y = ensureSpace(doc, y, 80);
    y = sectionTitle(doc, "Exterior — enabled categories", M, y);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: FOOTER_RESERVE },
      theme: "plain",
      headStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
        lineColor: COLORS.hairline, lineWidth: { bottom: 0.3 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.42 },
        1: { cellWidth: CONTENT_W * 0.18, textColor: COLORS.muted, halign: "right" },
        2: { cellWidth: CONTENT_W * 0.16, textColor: COLORS.muted, halign: "right" },
        3: { cellWidth: CONTENT_W * 0.24, halign: "right", fontStyle: "bold" },
      },
      head: [["Category", "Qty", "Scope", `Total (${cy})`]],
      body: extRows,
      foot: [["", "", "Exterior subtotal", stripCurrencySymbol(f(exterior))]],
      footStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.ink, fontSize: 9, fontStyle: "bold",
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // 3) Systems items
  const sysRows = (project.systems || []).filter((it) => it.enabled).map((it) => [
    it.label || titleCase(it.category),
    `${it.quantity || 0} ${it.unit || ""}`,
    titleCase(it.scope),
    stripCurrencySymbol(f(exteriorItemTotal(it))),
  ]);
  if (sysRows.length > 0) {
    y = ensureSpace(doc, y, 80);
    y = sectionTitle(doc, "Systems — enabled categories", M, y);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: FOOTER_RESERVE },
      theme: "plain",
      headStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
        lineColor: COLORS.hairline, lineWidth: { bottom: 0.3 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.42 },
        1: { cellWidth: CONTENT_W * 0.18, textColor: COLORS.muted, halign: "right" },
        2: { cellWidth: CONTENT_W * 0.16, textColor: COLORS.muted, halign: "right" },
        3: { cellWidth: CONTENT_W * 0.24, halign: "right", fontStyle: "bold" },
      },
      head: [["Category", "Qty", "Scope", `Total (${cy})`]],
      body: sysRows,
      foot: [["", "", "Systems subtotal", stripCurrencySymbol(f(systems))]],
      footStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.ink, fontSize: 9, fontStyle: "bold",
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // 4) Extensions
  const extensionsArr = project.extensions || [];
  if (extensionsArr.length > 0) {
    y = ensureSpace(doc, y, 80);
    y = sectionTitle(doc, "Extensions — shell + fitout", M, y);
    const extRows2 = [];
    for (const ext of extensionsArr) {
      const shellTotal = (ext.shell_items || []).reduce((s, it) => s + workItemTotal(it), 0);
      const fitoutTotal = (ext.fitout_rooms || []).reduce((s, r) => s + roomTotals(r).total, 0);
      extRows2.push([
        ext.name || titleCase(ext.type || ""),
        titleCase(ext.type || ""),
        ext.footprint_sqm ? `${ext.footprint_sqm} sqm` : "—",
        stripCurrencySymbol(f(shellTotal)),
        stripCurrencySymbol(f(fitoutTotal)),
        stripCurrencySymbol(f(shellTotal + fitoutTotal)),
      ]);
    }
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: FOOTER_RESERVE },
      theme: "plain",
      headStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
        lineColor: COLORS.hairline, lineWidth: { bottom: 0.3 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.26 },
        1: { cellWidth: CONTENT_W * 0.16, textColor: COLORS.muted },
        2: { cellWidth: CONTENT_W * 0.12, textColor: COLORS.muted, halign: "right" },
        3: { cellWidth: CONTENT_W * 0.14, halign: "right" },
        4: { cellWidth: CONTENT_W * 0.14, halign: "right" },
        5: { cellWidth: CONTENT_W * 0.18, halign: "right", fontStyle: "bold" },
      },
      head: [["Name", "Type", "Footprint", "Shell", "Fitout", `Total (${cy})`]],
      body: extRows2,
      foot: [["", "", "", "", "Extensions subtotal", stripCurrencySymbol(f(extensions))]],
      footStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.ink, fontSize: 9, fontStyle: "bold",
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // 5) Soft Costs
  const softRows = (project.soft_costs || []).filter((it) => it.enabled).map((it) => {
    const sci = softCostItemTotal(it, project, reno);
    const modeLabel = ({
      fixed: "Fixed",
      per_sqm: `${it.value || 0}/sqm × floor area`,
      per_room: `${it.value || 0}/room × room count`,
      annual_prorated: `Annual $ × ${Number(project?.timeline_months) || 4}/12 mo`,
      percent_purchase: `% of purchase (${it.value || 0}%)`,
      percent_bond: `% of bond (${it.value || 0}%)`,
      bond_interest_auto: "Bond interest (auto)",
      percent_sale: `% of sale (${it.value || 0}%)`,
      percent_renovation: `% of renovation (${it.value || 0}%)`,
    })[it.mode] || "—";
    return [it.label, modeLabel, stripCurrencySymbol(f(sci))];
  });
  if (softRows.length > 0) {
    y = ensureSpace(doc, y, 80);
    y = sectionTitle(doc, "Soft Costs", M, y);
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: FOOTER_RESERVE },
      theme: "plain",
      headStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.muted, fontSize: 8, fontStyle: "bold",
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
        lineColor: COLORS.hairline, lineWidth: { bottom: 0.3 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.45 },
        1: { cellWidth: CONTENT_W * 0.30, textColor: COLORS.muted },
        2: { cellWidth: CONTENT_W * 0.25, halign: "right", fontStyle: "bold" },
      },
      head: [["Item", "Basis", `Amount (${cy})`]],
      body: softRows,
      foot: [["", "Soft costs subtotal", stripCurrencySymbol(f(soft))]],
      footStyles: {
        fillColor: COLORS.bgSoft, textColor: COLORS.ink, fontSize: 9, fontStyle: "bold",
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // --- Footer + page numbers on every page -----------------------
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, PAGE_W, PAGE_H, M, i, totalPages);
  }

  // --- Save ------------------------------------------------------
  const safeName = (project.name || "project").replace(/[^a-z0-9-_ ]/gi, "").replace(/\s+/g, "-").toLowerCase();
  doc.save(`${safeName}-summary-report.pdf`);
}

// ----------------------------------------------------------- helpers

function sectionTitle(doc, label, x, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.ink);
  doc.text(label, x, y);
  doc.setDrawColor(...COLORS.hairline);
  doc.setLineWidth(0.4);
  doc.line(x, y + 6, x + 30, y + 6);
  return y + 16;
}

function drawPill(doc, x, y, text, fg, bg) {
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  const padX = 6;
  const w = doc.getTextWidth(text) + padX * 2;
  const h = 14;
  doc.setFillColor(...bg);
  doc.setDrawColor(...bg);
  doc.roundedRect(x, y, w, h, 5, 5, "F");
  doc.setTextColor(...fg);
  doc.text(text, x + padX, y + 9.5);
  return x + w;
}

function drawCostVsARV(doc, x, y, w, total, arv, f) {
  const max = Math.max(total, arv, 1);
  const barH = 22;
  const rowGap = 10;

  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.muted);
  doc.setFont("helvetica", "normal");
  doc.text("Total cost", x, y + 14);
  const totalW = (total / max) * (w - 110);
  doc.setFillColor(...COLORS.ink);
  doc.roundedRect(x + 60, y, totalW, barH, 3, 3, "F");
  doc.setTextColor(...COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(f(total), x + 60 + totalW + 6, y + 14);

  let cy = y + barH + rowGap;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.muted);
  doc.text("ARV", x, cy + 14);
  const arvW = (arv / max) * (w - 110);
  doc.setFillColor(...COLORS.sage);
  doc.roundedRect(x + 60, cy, arvW, barH, 3, 3, "F");
  doc.setTextColor(...COLORS.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(f(arv), x + 60 + arvW + 6, cy + 14);

  return cy + barH + 14;
}

function drawFeasibility(doc, x, y, w, { profit, roi, margin: m, feasibility, f }) {
  const t = tone(feasibility.tone);

  const bannerH = 36;
  doc.setFillColor(...t.bg);
  doc.setDrawColor(...t.bg);
  doc.roundedRect(x, y, w, bannerH, 6, 6, "F");
  doc.setTextColor(...t.fg);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(feasibility.label.toUpperCase(), x + 14, y + 23);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`ROI ${formatPctSafe(roi)} · Profit ${f(profit)}`, x + w - 14, y + 23, { align: "right" });

  let cy = y + bannerH + 12;
  const cellW = (w - 16) / 3;
  const items = [
    { label: "Projected profit", value: f(profit), tone: profit >= 0 ? "green" : "red" },
    { label: "ROI", value: formatPctSafe(roi), tone: roi >= 0.2 ? "green" : roi >= 0.1 ? "amber" : "red" },
    { label: "Margin", value: formatPctSafe(m), tone: m >= 0.2 ? "green" : m >= 0.1 ? "amber" : "red" },
  ];
  items.forEach((it, i) => {
    const cx = x + i * (cellW + 8);
    doc.setFillColor(...COLORS.bgSoft);
    doc.setDrawColor(...COLORS.hairline);
    doc.setLineWidth(0.4);
    doc.roundedRect(cx, cy, cellW, 52, 6, 6, "FD");
    doc.setTextColor(...COLORS.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(it.label.toUpperCase(), cx + 12, cy + 16);
    const stat = tone(it.tone);
    doc.setTextColor(...stat.fg);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(it.value, cx + 12, cy + 38);
  });

  return cy + 52 + 6;
}

function drawFooter(doc, pageW, pageH, margin, pageIdx, pageCount) {
  doc.setDrawColor(...COLORS.hairline);
  doc.setLineWidth(0.4);
  doc.line(margin, pageH - 36, pageW - margin, pageH - 36);
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.muted);
  doc.setFont("helvetica", "normal");
  doc.text(
    "Estimates only — not a professional valuation, quote or financial advice. Obtain independent professional advice and binding quotes before any financial commitment.",
    margin,
    pageH - 22,
    { maxWidth: pageW - margin * 2 - 90 }
  );
  doc.text(`FlipCost AI · Page ${pageIdx} / ${pageCount}`, pageW - margin, pageH - 22, { align: "right" });
}

function formatPctSafe(n) {
  if (!isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function titleCase(s) {
  if (!s) return "—";
  return String(s).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
